import { getEmployeePayrollId } from '@nxsys/utils/employee-payroll-id';
import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  defaultCisStatus,
  defaultDateLeftReason,
  defaultDocumentTypeOptions,
  defaultPaymentFrequency,
  defaultPaymentMethod,
  defaultRegistrationStatus,
  defaultRightToWorkStatus,
  defaultsBusinessTypeOptions,
  genderOptions,
  holidayPaymentMethodOptions,
  maritalStatusOption,
  paymentModelOptions,
  starterDeclarationOptions,
  starterTypeOptions,
  titleOptions,
} from '@nxsys/constants/AppConstants';
import {
  useCisVerify,
  useDeleteSubcontractor,
  useExportAllEmployee,
  useExportEmployee,
  useFetchSmartPension,
  useResendLoginIdMultiple,
  useUpdateOnHoldEmployee,
} from '@nxsys/hooks/api';
import useExportAllSocket from '@nxsys/hooks/useExportAllSocket';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { roles } from '@nxsys/store/constants';
import { APP_NAME_BUREAU, IRELAND } from '@nxsys/store/constants/keys';
import {
  employeeActions,
  EmployeeState,
} from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import {
  countryList,
  defaultMarginMethod,
  defaultPaymentTerm,
  formatStatus,
  showCurrencyByCountry,
} from '@nxsys/utils';
import {
  csvConfigExport,
  generateIrelandSubDataExport,
  EXPORT_BULK_UPDATE_EMPLOYEE_IRELAND_COLUMN,
} from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { Dropdown, Modal } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find, isEmpty, some } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EmployeesFilterModal, {
  EmployeesFilterValues,
} from '../components/EmployeesFilterModal';
import HoldEmployeeModal from '../components/HoldEmployeeModal';
import LeaverIrelandModal from '../components/LeaverIrelandModal';
import SyncPensionModal, {
  SyncPensionValues,
} from '../components/SyncPensionModal';
import CreateEmployeeModal from '../create';
import { getEmployeesColumns } from './employeesColumns';
import useInviteOnboarding from './useInviteOnboarding';

interface EmployeesListingProps {
  employeeList: EmployeeState['employeeList'];
  handleFetchData: (params: GetEmployeeListParams) => void;
}

const BaseEmployeesListing: React.FC<EmployeesListingProps> = ({
  employeeList,
  handleFetchData,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const { user, isAgency, payrollCountry, isBureauEmployer, isBureau } =
    useSelector((state: AppState) => state.appStore);

  const { list, pagination, loading, queryParams } = employeeList;

  const [selectedRows, setSelectedRows] = useState<EmployeeRecordType[]>([]);
  const [pendingResendIds, setPendingResendIds] = useState<string[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);
  const [isOpenSyncPensionModal, setIsOpenSyncPensionModal] = useState(false);
  const [isOpenCreateEmployeeModal, setIsOpenCreateEmployeeModal] =
    useState(false);
  const [isOpenLeaverModal, setIsOpenLeaverModal] = useState(false);

  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const isEmployer = isBureauEmployer ?? false;

  const renderTitle = useCallback(
    () => (
      <AppPageTitle
        prefix={messages['common.employees'] || 'Employees'}
        separator=""
        title=""
      />
    ),
    [messages],
  );

  const renderRightContent = () => {
    if (isAgency) return null;

    const items = [
      {
        key: 'addNew',
        label: messages['common.addNew'],
        onClick: () => setIsOpenCreateEmployeeModal(true),
      },
      ...(payrollCountry !== IRELAND
        ? [
            {
              key: 'syncPension',
              label: messages['common.syncPensionData'],
              onClick: () => setIsOpenSyncPensionModal(true),
            },
          ]
        : []),
      {
        key: 'resendLoginId',
        label: messages['common.resendLoginId'],
        onClick: handleResendLoginIdSelected,
      },
      ...(user?.role === roles.admin
        ? [
            {
              key: 'cisVerify',
              label: messages['common.verify'],
              onClick: handleCisVerify,
            },
          ]
        : []),
      ...(payrollCountry === IRELAND
        ? [
            {
              key: 'leaver',
              label: messages['common.leaver'] ?? 'Leaver',
              onClick: () => setIsOpenLeaverModal(true),
            },
          ]
        : []),
      {
        key: 'sendOnboarding',
        label: 'Invite Onboarding',
        onClick: handleSendOnboardingSelected,
      },
      {
        key: 'delete',
        label: 'Delete',
        onClick: handleDeleteSelected,
      },
    ];

    const exportMenuItems = [
      {
        key: 'export',
        label: messages['common.export'],
        onClick: handleExportSelected,
      },
      {
        key: 'exportAll',
        label: messages['common.exportAll'],
        onClick: handleExportAll,
      },
    ];

    return (
      <div className="flex items-center gap-x-4">
        <Dropdown menu={{ items: exportMenuItems }}>
          <AppButton
            type="primary"
            name={messages['common.export']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
        <Dropdown menu={{ items }}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    handleFetchData({ page: 0, size: 20 });
  }, []);

  const refetch = useCallback(() => {
    handleFetchData({ ...queryParams });
  }, [handleFetchData, queryParams]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: EmployeesFilterValues) => {
    const params: GetEmployeeListParams = {
      ...queryParams,
      page: 0,
      status:
        values.status && values.status !== 'NOT_ONBOARDING'
          ? [values.status]
          : undefined,
      isNotOnboarding: values.isNotOnboarding,
      registrationStatus: values.registrationStatus || undefined,
      taxBasis: values.taxBasis?.length ? values.taxBasis : undefined,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const { run: exportEmployee, loading: isExportEmployeeLoading } =
    useExportEmployee();

  const handleExportSelected = async () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    const ids = selectedRows.map((item) => item.id);
    const res = await exportEmployee({ page: 0, size: 20, ids });

    // Ireland bureau: export Ireland-specific columns (PPS, Eircode, RCT, PRSI,
    // USC/LPT YTD, Tax Credit, etc.) to match the server-side Export All. The
    // UK header/data build below only produces UK columns. (NX-3962)
    if (payrollCountry === IRELAND) {
      const irelandData = generateIrelandSubDataExport(res?.data ?? []);
      const irelandExportConfig = csvConfigExport(
        'Employees-export',
        EXPORT_BULK_UPDATE_EMPLOYEE_IRELAND_COLUMN,
      );
      new ExportToCsv(irelandExportConfig).generateCsv(irelandData);
      return;
    }

    const dataExport = (res?.data ?? []).map((item) => {
      const {
        title,
        gender,
        DOB,
        status,
        agencyRef,
        forename,
        surname,
        mobile,
        email,
        address,
        city,
        county,
        country,
        postCode,
        emergencyName,
        emergencyAddress,
        emergencyCity,
        emergencyCounty,
        emergencyPostCode,
        emergencyCountry,
        emergencyMobile,
        emergencyEmail,
        payFrequency,
        registrationStatus,
        maritalStatus,
        NINumber,
        nationality,
        payMethod,
        jobDescription,
        rateHour,
        hours,
        annualSalary,
        dateOfJoining,
        dateLeft,
        reason,
        allowPortalLogin,
        loginId,
        rightToWorkStatus,
        documentType,
        documentExpiryDate,
        bankRef,
        prospectOnboarding,
        primaryAgency,
        secondaryAgency,
        companyPayment,
        setting,
        latestPayroll,
        pensionMethodText,
        nestGroupText,
      } = item;
      return {
        id: item.id || '',
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${
          agencyRef || NINumber || item.id || ''
        }`,
        isOnboarding: prospectOnboarding ? 'Yes' : 'No',
        title: title
          ? find(titleOptions, (t) => t.value === title)?.label || ''
          : '',
        forename: forename || '',
        surname: surname || '',
        DOB: DOB ? moment(DOB).format('DD/MM/YYYY') : '',
        gender: gender
          ? find(genderOptions, (t) => t.value === gender)?.label || ''
          : '',
        maritalStatus: maritalStatus
          ? find(maritalStatusOption, (m) => m.value === maritalStatus)
              ?.label || ''
          : '',
        NINumber: NINumber || '',
        nationality: nationality || '',
        address1: !isEmpty(address) ? address[0] || '' : '',
        address2: !isEmpty(address) ? address[1] || '' : '',
        address3: !isEmpty(address) ? address[2] || '' : '',
        city: city || '',
        county: county || '',
        postCode: postCode || '',
        country: country
          ? find(countryList, (c) => c?.value === country)?.label
          : '',
        mobile: mobile || '',
        email: email || '',
        status: formatStatus(status) || '',
        registrationStatus: registrationStatus
          ? find(
              defaultRegistrationStatus,
              (reg) => reg.value === registrationStatus,
            )?.label
          : '',
        payFrequency: payFrequency
          ? find(defaultPaymentFrequency, (reg) => reg.value === payFrequency)
              ?.label
          : '',
        payMethod: payMethod
          ? find(defaultPaymentMethod, (p) => p.value === payMethod)?.label
          : '',
        jobDescription: jobDescription || '',
        rateHour: rateHour || '',
        hours: hours || '',
        ...(isBureau ? { annualSalary: annualSalary || '' } : {}),
        dateOfJoining: dateOfJoining
          ? moment(dateOfJoining).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        dateLeft: dateLeft
          ? moment(dateLeft).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        reason: reason
          ? find(defaultDateLeftReason, (rs) => rs.value === reason)?.label
          : '',
        allowPortalLogin: allowPortalLogin ? 'Yes' : 'No',
        loginId: loginId || '',
        rightToWorkStatus: rightToWorkStatus
          ? find(defaultRightToWorkStatus, (r) => r.value === rightToWorkStatus)
              ?.label
          : '',
        documentType: documentType
          ? find(defaultDocumentTypeOptions, (d) => d.value === documentType)
          : '',
        documentExpiryDate: documentExpiryDate
          ? moment(documentExpiryDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        bankName: bankRef?.bankName || '',
        accountName: bankRef?.accountName || '',
        accountNumber: bankRef?.accountNumber || '',
        sortCode: bankRef?.sortCode || '',
        buildingSocietyNumber: bankRef?.buildingSocietyNumber || '',
        IBANReference: bankRef?.IBANReference || '',
        SWIFTNo: bankRef?.SWIFTNo || '',
        paymentTerm: setting?.paymentTerm
          ? find(defaultPaymentTerm, (pt) => pt.value === setting?.paymentTerm)
              ?.label
          : '',
        paymentModel: setting?.paymentModel
          ? find(
              paymentModelOptions,
              (pt) => pt.value === setting?.paymentModel,
            )?.label
          : '',
        businessType: setting?.businessType
          ? find(
              defaultsBusinessTypeOptions,
              (bs) => bs.value === setting?.businessType,
            )?.label
          : '',
        cisStatus: setting?.cisStatus
          ? find(defaultCisStatus, (cs) => cs.value === setting?.cisStatus)
              ?.label
          : '',
        PVNo: setting?.personalVerificationNumber || '',
        VATRegNo: setting?.VATRegNo || '',
        verificationDate: setting?.verificationDate
          ? moment(setting?.verificationDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '',
        matchDate: setting?.matchDate
          ? moment(setting?.matchDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        companyName: setting?.companyName || '',
        registrationNumber: setting?.registrationNumber || '',
        UTRNo: setting?.UTRNo || '',
        firmName: setting?.firmName || '',
        partnersName: setting?.partnersName || '',
        partnersCategory: setting?.partnersCategory || '',
        taxCode: setting?.taxCode || '',
        taxBasis: setting?.taxBasis === 'W1M1' ? 'W1M1' : 'Cumulative',
        niCategory: setting?.niCategory || '',
        starterType: setting?.starterType
          ? find(starterTypeOptions, (st) => st.value === setting?.starterType)
              ?.label
          : '',
        starterDeclaration: setting?.starterDeclaration
          ? find(
              starterDeclarationOptions,
              (st) => st.value === setting?.starterDeclaration,
            )?.label
          : '',
        p45GrossToDate: setting?.p45GrossToDate || '',
        p45TaxToDate: setting?.p45TaxToDate || '',
        isApprentice: setting?.isApprentice ? 'Yes' : 'No',
        previousPayrollNo: setting?.previousPayrollNo || '',
        grossForNIYTD: setting?.ytdValues?.grossForNI || 0,
        grossForTaxYTD: setting?.ytdValues?.grossForTax || 0,
        taxYTD: setting?.ytdValues?.tax || 0,
        eeNIYTD: setting?.ytdValues?.eeNI || 0,
        erNIYTD: setting?.ytdValues?.erNI || 0,
        eePensionYTD: setting?.ytdValues?.eePension || 0,
        erPensionYTD: setting?.ytdValues?.erPension || 0,
        pensionMethodText: pensionMethodText || '',
        nestGroupText: nestGroupText || '',
        stLoanYTD: setting?.ytdValues?.stLoan || 0,
        pgLoanYTD: setting?.ytdValues?.pgLoan || 0,
        smpYTD: setting?.ytdValues?.smp || 0,
        sncpYTD: setting?.ytdValues?.sncp || 0,
        sspYTD: setting?.ytdValues?.ssp || 0,
        sapYTD: setting?.ytdValues?.sap || 0,
        sppaYTD: setting?.ytdValues?.sppa || 0,
        sppYTD: setting?.ytdValues?.spp || 0,
        atLelTTD: setting?.ytdValues?.atLel || 0,
        lelToPTYTD: setting?.ytdValues?.lelToPT || 0,
        ptToUELYTD: setting?.ytdValues?.ptToUEL || 0,
        companyPayment: companyPayment?.id || '',
        primaryAgency: primaryAgency?.id || '',
        secondaryAgency: secondaryAgency?.id || '',
        marginMethod: setting?.marginMethod
          ? find(
              defaultMarginMethod,
              (reg) => reg.value === setting?.marginMethod,
            )?.label
          : '',
        margin: setting?.margin || '',
        marginCap: setting?.marginCapped || '',
        assignmentRequired: setting?.assignmentRequired ? 'Yes' : 'No',
        timesheetApprovalNeeded: formatStatus(
          setting?.timesheetApprovalNeeded || '',
        ),
        holidayPayMethod: setting?.holidayPayMethod
          ? find(
              holidayPaymentMethodOptions,
              (hp) => hp.value === setting?.holidayPayMethod,
            )?.label
          : '',
        holidayPayRate: setting?.holidayPayRate || '',
        holidayPayOpeningBalance: setting?.holidayPayOpeningBalance || 0,
        retainedHolidayPayAppLevy: setting?.retainedHolidayPayAppLevy || 0,
        retainedHolidayPayERNI: setting?.retainedHolidayPayERNI || 0,
        retainedHolidayPayErPen: setting?.retainedHolidayPayErPen || 0,
        customerSuccessManager: '',
        agencyConsultant: '',
        salesConsultant: '',
        other: '',
        emergencyName: emergencyName || '',
        emergencyAddress1: emergencyAddress ? emergencyAddress[0] : '',
        emergencyAddress2: emergencyAddress ? emergencyAddress[1] : '',
        emergencyAddress3: emergencyAddress ? emergencyAddress[2] : '',
        emergencyMobile: emergencyMobile || '',
        emergencyEmail: emergencyEmail || '',
        emergencyCity: emergencyCity || '',
        emergencyCounty: emergencyCounty || '',
        emergencyPostCode: emergencyPostCode || '',
        emergencyCountry: emergencyCountry
          ? find(countryList, (c) => c?.value === emergencyCountry)?.label
          : '',
        lastPaidDate: latestPayroll?.paymentDate
          ? moment(latestPayroll?.paymentDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '',
        lastPaidPeriod:
          latestPayroll?.taxPeriod && latestPayroll?.taxYear
            ? ` ${latestPayroll?.taxPeriod}/${latestPayroll?.taxYear}`
            : '',
        payrollId: getEmployeePayrollId(item),
      };
    });

    const headers = [
      'Employee ID',
      'Agency Ref',
      'Onboarding Required',
      'Title',
      'Forename',
      'Surname',
      'DOB',
      'Gender',
      'Marital Status',
      'NI Number', // unreachable for Ireland: handleExportSelected returns early above for isIreland
      'Nationality',
      'Address1',
      'Address2',
      'Address3',
      'City/Town',
      'County',
      'Post Code', // unreachable for Ireland: handleExportSelected returns early above for isIreland
      'Country',
      'Mobile',
      'Email',
      'Status',
      'Registration Status',
      'Pay Frequency',
      'Pay Method',
      'Job Description',
      'Rate/Hour',
      'Hours',
      ...(isBureau ? ['Annual Salary'] : []),
      'Date of Joining',
      'Date Left',
      'Reason',
      'Allow Portal Login?',
      'Login ID',
      'Right to Work Status',
      'Document Type',
      'Document Expiry Date',
      'Bank Name',
      'Account name',
      'Account Number',
      'Sort Code',
      'Building Society Number',
      'SWIFT No',
      'IBAN Reference',
      'Payment Term',
      'Payment Model',
      'Business Type',
      'CIS Status',
      'Personal Verification Number',
      'VAT Reg No',
      'Verification Date',
      'Match Date',
      'Company Name',
      'Registration Number',
      'UTR No',
      'Firm Name',
      'Partner Name',
      'Partner Category',
      'Tax Code',
      'Tax Basis',
      'NI Category',
      'Starter Type',
      'Starter Declaration',
      'P45 Gross To Date',
      'P45 Tax To Date',
      'Is Apprentice',
      'Previous Payroll Number',
      'Gross for NI YTD',
      'Gross for Tax (YTD)',
      'Tax (YTD)',
      'Employee NI (YTD)',
      'Employer NI (YTD)',
      'Employee Pension (YTD)',
      'Employer Pension (YTD)',
      'Pension Method',
      'NEST Group',
      'Student Loan (YTD)',
      'PG Student Loan (YTD)',
      'SMP (YTD)',
      'SNCP (YTD)',
      'SSP (YTD)',
      'SAP (YTD)',
      'SPPA (YTD)',
      'SPP (YTD)',
      'Gross to LEL',
      'Gross LEL to PT',
      'Gross PT to UEL',
      'Payment Company',
      'Primary Agency',
      'Secondary Agency',
      'Margin Method',
      'Margin',
      'Margin Cap',
      'Assignment Required?',
      'Timesheet Approval Needed?',
      'Holiday Pay Method',
      'Holiday Pay Rate',
      'Holiday Pay Opening Balance',
      'Holiday Pay App Levy',
      'Holiday Pay ERNI',
      "Holiday Pay Employer's Pension",
      'Customer Success Manager',
      'Agency Consultant',
      'Sales Consultant',
      'Other',
      'Emergency Contact Name',
      'Emergency Contact Address 1',
      'Emergency Contact Address 2',
      'Emergency Contact Address 3',
      'Emergency Contact Mobile',
      'Emergency Contact Email',
      'Emergency Contact City/Town',
      'Emergency Contact County',
      'Emergency Contact Post Code',
      'Emergency Contact Country',
      'Last Paid Date',
      'Last Paid Period',
      'Payroll ID',
    ];

    const exportName = csvConfigExport('Employees-export', headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  useExportAllSocket({
    eventName: `export-all-process-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        window.open(data.link, '_blank');
        showMessage({
          type: 'success',
          content: messages['common.exportAllSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: data?.error?.message || 'System Error',
        });
      }
    },
  });

  const { run: exportAllEmployee, loading: isExportAllEmployeeLoading } =
    useExportAllEmployee();
  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    exportAllEmployee({ ...queryParams, size: pagination.totalDocs }).then(
      () => {
        showMessage({
          type: 'info',
          content: messages['common.exportAllPending'],
        });
      },
    );
  };

  useNotificationSocket({
    eventName: `resend-mail-login-id-${user?.id}`,
    onSuccess: (data) => {
      if (data?.success) {
        showMessage({
          type: 'success',
          content: messages['common.resendLoginIdSuccess'],
        });
        refetch();
      }
    },
  });

  const {
    run: resendLoginIdMultiple,
    loading: isResendLoginIdMultipleLoading,
  } = useResendLoginIdMultiple();
  const handleResendLoginIdMultiple = (data: EmployeeRecordType[]) => {
    const ids = data.map((item) => item.id);
    setPendingResendIds(ids);
    resendLoginIdMultiple({ ids })
      .then(() => {
        showMessage({
          type: 'info',
          content: messages['common.resendLoginIdPending'],
        });
      })
      .finally(() => {
        setPendingResendIds([]);
      });
  };
  const handleResendEmailLoginId = (id: string) => {
    const record = list?.find((r) => r.id === id);
    if (record) handleResendLoginIdMultiple([record]);
  };

  const handleResendLoginIdSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    handleResendLoginIdMultiple(selectedRows);
  };

  const {
    handleSendOnboarding,
    handleSendOnboardingSelected: sendOnboardingSelected,
    isInviteOnboardingLoading,
  } = useInviteOnboarding(refetch);

  const handleSendOnboardingSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    sendOnboardingSelected(selectedRows);
  };

  const { run: deleteSubcontractor, loading: isDeleteLoading } =
    useDeleteSubcontractor();
  const handleConfirmDelete = (employee: EmployeeRecordType | null) => {
    const ids = employee ? [employee.id] : selectedRows.map((r) => r.id);
    const count = ids.length;

    Modal.confirm({
      title: messages['common.confirm'] || 'Confirm',
      content: `Are you sure you want to delete ${count} employee(s)?`,
      onOk: async () => {
        const res = await deleteSubcontractor({ ids });
        if (res?.success) {
          showMessage({ type: 'success', content: 'Deleted successfully' });
        }
        if (res?.failed?.length) {
          showMessage({
            type: 'warning',
            content: `Cannot delete employees with existing timesheets or salaries: ${res.failed.join(', ')}`,
          });
        }
        setSelectedRows([]);
        refetch();
        // Deleting an employee closes their onboarding prospect.
        dispatch(prospectActions.getProspectPendingCount());
      },
    });
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    handleConfirmDelete(null);
  };

  const { run: cisVerify, loading: isCisVerifyLoading } = useCisVerify();
  const handleCisVerify = async () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: messages['common.noSelected'] });
      return;
    }
    const companyId = selectedRows[0]?.companyPayment?.id;
    if (some(selectedRows, (item) => item?.companyPayment?.id !== companyId)) {
      showMessage({
        type: 'error',
        content:
          messages[
            'common.selectedSubcontractorsDoNotHaveTheSamePaymentCompany'
          ],
      });
      return;
    }
    await cisVerify({
      companyId: companyId || '',
      subcontractorsRef: selectedRows.map((item) => item.id),
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    });
    refetch();
  };

  useNotificationSocket({
    eventName: `fetch-smart-pension-by-company-${user?.id}`,
    onSuccess: (data) => {
      if (data.success) {
        showMessage({
          type: 'success',
          content: messages['common.syncPensionDataSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content:
            data?.error?.message || messages['common.syncPensionDataFailed'],
        });
      }
    },
  });

  const { run: fetchSmartPension, loading: isFetchSmartPensionLoading } =
    useFetchSmartPension();
  const handleSyncPension = async (values: SyncPensionValues) => {
    const res = await fetchSmartPension({
      companyId: values.company?.value || '',
      hasRunJob: true,
    });
    if (res.success) {
      showMessage({
        type: 'info',
        content: messages['common.syncPensionDataPending'],
      });
    }
  };

  const [holdEmployeeModal, setHoldEmployeeModal] = useState<{
    isOpen: boolean;
    data: EmployeeRecordType | null;
  }>({ isOpen: false, data: null });

  const { run: updateOnHoldEmployee, loading: isUpdateOnHoldEmployeeLoading } =
    useUpdateOnHoldEmployee();
  const handleOnHoldEmployee = async () => {
    const res = await updateOnHoldEmployee(holdEmployeeModal.data?.id || '', {
      isOnHold: true,
      holdReason: null,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.employeeOnHoldSuccess'],
      });
      refetch();
    }
  };

  const handleUnHoldEmployee = async (employee: EmployeeRecordType) => {
    const res = await updateOnHoldEmployee(employee?.id || '', {
      isOnHold: false,
      holdReason: null,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.employeeUnHoldSuccess'],
      });
      refetch();
    }
  };

  const handleViewDetailClick = (id: string) => {
    navigate(`/employees-subcontractors/${id}`);
  };

  const handleAddHours = (employee: EmployeeRecordType) => {
    navigate('/timesheets/add-new', {
      query: {
        from: '/employees',
        defaultValues: JSON.stringify({
          subcontractorRef: {
            label:
              employee?.fullName || `${employee.forename} ${employee.surname}`,
            value: employee?.id,
            fullData: employee,
          },
        }),
      },
    });
  };

  const handleOnHold = (employee: EmployeeRecordType) => {
    if (employee?.isOnHold) {
      handleUnHoldEmployee(employee);
    } else {
      setHoldEmployeeModal({ isOpen: true, data: employee });
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isExportEmployeeLoading ||
        isExportAllEmployeeLoading ||
        isResendLoginIdMultipleLoading ||
        isCisVerifyLoading ||
        isFetchSmartPensionLoading ||
        isUpdateOnHoldEmployeeLoading ||
        isInviteOnboardingLoading ||
        isDeleteLoading
      }
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 3000, y: 700 }}
            columns={getEmployeesColumns({
              messages,
              user,
              payrollCountry: payrollCountry || 'UNITED_KINGDOM',
              isEmployer,
              currencySymbol,
              handleViewDetailClick,
              handleAddHours,
              handleOnHold,
              handleResendEmailLoginId,
              handleConfirmDelete,
              handleSendOnboarding,
              isSending: isResendLoginIdMultipleLoading,
              selectedResends: pendingResendIds,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selected: EmployeeRecordType[]) => {
                setSelectedRows(selected);
              },
            }}
            onChangeTable={onChangeTable}
          />
        </AppsContent>
      </AppCard>
      <EmployeesFilterModal
        title={messages['common.filter']}
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        payrollCountry={payrollCountry ?? 'UNITED_KINGDOM'}
      />
      <SyncPensionModal
        isOpen={isOpenSyncPensionModal}
        toggle={() => setIsOpenSyncPensionModal(false)}
        onSubmit={handleSyncPension}
      />
      <LeaverIrelandModal
        isOpen={isOpenLeaverModal}
        toggle={() => setIsOpenLeaverModal(false)}
      />
      <HoldEmployeeModal
        isOpen={holdEmployeeModal.isOpen}
        toggle={() =>
          setHoldEmployeeModal({
            ...holdEmployeeModal,
            isOpen: !holdEmployeeModal.isOpen,
          })
        }
        onSubmit={handleOnHoldEmployee}
      />
      <CreateEmployeeModal
        isOpen={isOpenCreateEmployeeModal}
        toggle={() => setIsOpenCreateEmployeeModal(false)}
      />
    </AppsContainer>
  );
};

const EmployeesListing: React.FC = () => {
  const dispatch = useDispatch();
  const { employeeList } = useSelector(
    (state: AppState) => state.employeeStore,
  );

  const { isBureau, user } = useSelector((state: AppState) => state.appStore);

  const handleFetchData = (params: GetEmployeeListParams) => {
    dispatch(
      employeeActions.getEmployeeList({
        ...params,
        companyIds: isBureau ? user?.companyUserIds || [] : undefined,
        appName: isBureau ? APP_NAME_BUREAU : undefined,
      }),
    );
  };

  return (
    <BaseEmployeesListing
      employeeList={employeeList}
      handleFetchData={handleFetchData}
    />
  );
};

export default EmployeesListing;
